import * as layout from '../../components/markLayout.module.css';
import { MarkButton } from '../../components/MarkControls';
import React, { useCallback } from 'react';
import { useSearchParams, useNavigate, Outlet } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import { MarkPageHeader, MarkEmptyState } from '../../components/MarkLayout';

import usePagerProps from '../../../../hooks/usePagerProps';
import useDrips from '../../api/useDrips';
import DripsList from './DripsList';
import LimitItems from '../../../../components/LimitItems';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import type { DripStatus } from '../../types';
const STATUS_CHIPS: {
    value: DripStatus | '';
    label: string;
}[] = [
    { value: '', label: 'All' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PAUSED', label: 'Paused' }
];
const ALLOWED_STATUSES = new Set<string>(['DRAFT', 'ACTIVE', 'PAUSED']);
const clampInt = (raw: number, fallback: number, min: number, max: number): number =>
    Number.isFinite(raw) && raw >= min ? Math.min(Math.floor(raw), max) : fallback;
const DripsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const offset = clampInt(Number(searchParams.get('offset') || 0), 0, 0, 100000);
    const limit = clampInt(Number(searchParams.get('limit') || 20), 20, 1, 100);
    const rawStatus = searchParams.get('status');
    const statusFilter =
        rawStatus && ALLOWED_STATUSES.has(rawStatus) ? (rawStatus as DripStatus) : undefined;
    const {
        data: dripsResponse,
        isFetching,
        isLoading,
        error
    } = useDrips({
        offset,
        limit,
        status: statusFilter
    });
    const drips = dripsResponse?.data?.drips || [];
    const totalCount = dripsResponse?.data?.total_count || 0;
    const handleStatusChange = useCallback(
        (status: DripStatus | '') => {
            setSearchParams((prev) => {
                if (status) {
                    prev.set('status', status);
                } else {
                    prev.delete('status');
                }
                prev.delete('offset');
                return prev;
            });
        },
        [setSearchParams]
    );
    const pagerProps = usePagerProps({
        total_count: totalCount,
        limit,
        offset
    });
    return (
        <Section subtheme={['regular', 'background']} className={layout.page} noDefaultPadding>
            <MarkPageHeader
                title="Drips"
                description="Build automated journeys, manage enrollment, and monitor campaign performance."
            >
                <MarkButton
                    variant="primary"
                    size="regular"
                    onClick={() =>
                        navigate({ pathname: '/mark/drips/new', search: searchParams.toString() })
                    }
                >
                    New drip
                </MarkButton>
            </MarkPageHeader>
            <div
                className={layout.actions}
                style={{ justifyContent: 'flex-start' }}
                role="group"
                aria-label="Filter by status"
            >
                {STATUS_CHIPS.map((chip) => (
                    <MarkButton
                        key={chip.value || 'all'}
                        type="button"
                        onClick={() => handleStatusChange(chip.value)}
                        aria-pressed={(statusFilter || '') === chip.value}
                    >
                        {chip.label}
                    </MarkButton>
                ))}
            </div>
            <LimitItems
                isLoading={isLoading}
                limit={limit}
                offset={offset}
                total_count={totalCount}
            />
            {error ? (
                <QueryInlineError error={error} />
            ) : isLoading ? (
                <InlineLoader />
            ) : drips.length === 0 ? (
                <MarkEmptyState
                    title={statusFilter ? 'No drips with this status' : 'No drips yet'}
                    description={
                        statusFilter
                            ? 'Try another status or clear the filter to see all drips.'
                            : 'Create your first drip to get started.'
                    }
                >
                    {statusFilter && (
                        <MarkButton onClick={() => handleStatusChange('')}>Clear filter</MarkButton>
                    )}
                </MarkEmptyState>
            ) : (
                <DripsList drips={drips} isLoading={isFetching} pagerProps={pagerProps} />
            )}
            <Outlet />
        </Section>
    );
};
export default DripsPage;
